import React from 'react';
import { ChevronUpIcon, ChevronDownIcon, ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';

interface DataTableProps {
  children: React.ReactNode;
  className?: string;
}

interface DataTableHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface DataTableBodyProps {
  children: React.ReactNode;
  className?: string;
}

interface DataTableRowProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

interface DataTableCellProps {
  children: React.ReactNode;
  className?: string;
  colSpan?: number;
}

export function DataTable({ children, className = '' }: DataTableProps) {
  return (
    <div className={cn('border-border bg-card overflow-hidden rounded-lg border', className)}>
      <div className="relative overflow-x-auto">
        <table className="min-w-full">{children}</table>
      </div>
    </div>
  );
}

export function DataTableHeader({ children, className = '' }: DataTableHeaderProps) {
  return <thead className={cn('bg-surface', className)}>{children}</thead>;
}

export function DataTableBody({ children, className = '' }: DataTableBodyProps) {
  return <tbody className={cn('bg-card', className)}>{children}</tbody>;
}

export function DataTableRow({ children, className = '', onClick }: DataTableRowProps) {
  const baseClasses = 'border-b border-border transition-colors hover:bg-surface';
  const clickableClasses = onClick ? 'cursor-pointer' : '';

  return (
    <tr className={cn(baseClasses, clickableClasses, className)} onClick={onClick}>
      {children}
    </tr>
  );
}

export function DataTableCell({ children, className = '', colSpan }: DataTableCellProps) {
  return (
    <td className={cn('text-foreground px-5 py-4 text-sm', className)} colSpan={colSpan}>
      {children}
    </td>
  );
}

export function DataTableHeaderCell({ children, className = '', colSpan }: DataTableCellProps) {
  return (
    <th
      className={cn(
        'text-muted-foreground px-5 py-4 text-left text-xs font-medium tracking-wider uppercase',
        className,
      )}
      colSpan={colSpan}
    >
      {children}
    </th>
  );
}

export type SortDirection = 'asc' | 'desc';

export interface DataTableSortableHeaderProps {
  sortKey: string;
  currentSortKey: string | null;
  direction: SortDirection;
  onSort: (key: string) => void;
  children: React.ReactNode;
  className?: string;
  align?: 'left' | 'center' | 'right';
}

export function DataTableSortableHeader({
  sortKey,
  currentSortKey,
  direction,
  onSort,
  children,
  className = '',
  align = 'left',
}: DataTableSortableHeaderProps) {
  const isActive = currentSortKey === sortKey;
  const alignClass =
    align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';

  return (
    <th
      aria-sort={isActive ? (direction === 'asc' ? 'ascending' : 'descending') : undefined}
      className={cn(
        'text-muted-foreground px-5 py-4 text-xs font-medium tracking-wider uppercase',
        alignClass,
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="hover:text-foreground focus-visible:ring-ring inline-flex items-center gap-1.5 rounded focus-visible:ring-2 focus-visible:outline-none"
      >
        {children}
        {isActive ? (
          direction === 'asc' ? (
            <ChevronUpIcon className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronDownIcon className="h-4 w-4" aria-hidden />
          )
        ) : (
          <ArrowsUpDownIcon className="h-4 w-4" aria-hidden />
        )}
      </button>
    </th>
  );
}
